import axios from "axios";

import {
  getUserRecordComponentOptions,
  IUserRecord,
  IUserRecordDto,
  IUserRecordOption,
  IUserRecordOptions,
  IUserRecordOptionsDto,
} from "./UserRecordsModels";
import APP_CONFIG from "../app/configuration/AppConfig";

function convertUserRecordDtoToUserRecord(userRecordDto: IUserRecordDto): IUserRecord {
  const startDate = userRecordDto.EventDate ?? 0;
  const exclusionReason = userRecordDto.ExclusionReason ?? "";
  const otherReasonDescription = userRecordDto.ExclusionManualReason ?? "";
  const isEngineerOnly = userRecordDto.IsEngineerOnly ?? false;
  const isExclusion = userRecordDto.IsExclusion ?? false;
  const event = userRecordDto.Title ?? "";

  return {
    isExclusion,
    event,
    recordMetadata: userRecordDto.RecordMetadata ?? null,
    reportingSystem: userRecordDto.ReportingSystem ?? null,
    startDate,
    endDate: userRecordDto.EndDate ?? null,
    description: userRecordDto.Description ?? "",
    reporter: userRecordDto.Reporter ?? "",
    componentType: userRecordDto.ComponentType ?? "",
    componentName: userRecordDto.ComponentName ?? "",
    exclusionReason,
    otherReasonDescription,
    id: userRecordDto.Id,
    isEngineerOnly,
  };
}

function convertOptionsDtoToOptions(optionsDto: IUserRecordOptionsDto): IUserRecordOptions {
  const toOptions = (options: IUserRecordOptionsDto["Components"]): IUserRecordOption[] =>
    (options ?? []).map((option) => ({
      value: option.Value,
      label: option.Label,
    }));

  return {
    components:
      toOptions(optionsDto.Components).length > 0
        ? toOptions(optionsDto.Components)
        : getUserRecordComponentOptions(),
    exclusionReasons: toOptions(optionsDto.ExclusionReasons),
  };
}

async function queryCurrentUserRecords(siteId: string, creationTimeStart: Date, creationTimeEnd: Date) {
  const url = `${APP_CONFIG.serviceUrls.userRecordsUrlPrefix}/site/${siteId}/UserRecord`;
  const config = {
    method: "GET",
    url,
    params: {
      creationTimeStart: creationTimeStart.getTime(),
      creationTimeEnd: creationTimeEnd.getTime(),
    },
  };
  const httpResponse = await axios.request<IUserRecordDto[]>(config);
  return httpResponse.data.map((userRecordDto: IUserRecordDto) =>
    convertUserRecordDtoToUserRecord(userRecordDto)
  );
}

export async function addNewUserRecord(siteId: string, record: IUserRecordDto): Promise<IUserRecord> {
  const url = `${APP_CONFIG.serviceUrls.userRecordsUrlPrefix}/site/${siteId}/UserRecord`;
  const httpResponse = await axios.post<IUserRecordDto>(url, record);
  const res = convertUserRecordDtoToUserRecord(httpResponse.data);
  return res;
}

export async function updateUserRecord(
  siteId: string,
  recordId: number,
  record: IUserRecordDto
): Promise<IUserRecord> {
  const url = `${APP_CONFIG.serviceUrls.userRecordsUrlPrefix}/site/${siteId}/UserRecord/${recordId}`;
  const httpResponse = await axios.put<IUserRecordDto>(url, record);
  return convertUserRecordDtoToUserRecord(httpResponse.data);
}

export async function queryRecordOptions(): Promise<IUserRecordOptions> {
  const url = `${APP_CONFIG.serviceUrls.userRecordsUrlPrefix}/userRecord/options`;
  const httpResponse = await axios.get<IUserRecordOptionsDto>(url);
  return convertOptionsDtoToOptions(httpResponse.data);
}

export async function queryUserRecords(siteId: string, creationTimeStart: Date, creationTimeEnd: Date) {
  return queryCurrentUserRecords(siteId, creationTimeStart, creationTimeEnd);
}

export async function deleteUserRecord(siteId: string, recordId: number): Promise<void> {
  const url = `${APP_CONFIG.serviceUrls.userRecordsUrlPrefix}/site/${siteId}/UserRecord/${recordId}`;
  await axios.delete(url);
}

export async function mergeUserRecordsAsExclusion(
  siteId: string,
  activatedRecordId: number,
  deactivatedRecordId: number,
  exclusionReason: string,
  exclusionManualReason: string | null
): Promise<IUserRecord> {
  const url = `${APP_CONFIG.serviceUrls.userRecordsUrlPrefix}/site/${siteId}/UserRecord/merge-exclusion`;
  const httpResponse = await axios.post<IUserRecordDto>(url, {
    ActivatedRecordId: activatedRecordId,
    DeactivatedRecordId: deactivatedRecordId,
    ExclusionReason: exclusionReason,
    ExclusionManualReason: exclusionManualReason,
  });
  return convertUserRecordDtoToUserRecord(httpResponse.data);
}
